import { Plural, Trans } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconShipmentDone from '@/icons/ShipmentDone.svg';
import IconStar from '@/icons/Star.svg';
import { BrandRequestMainContent } from '@/layouts/Brand/BrandRequestLayout/BrandRequestLayout';
import { RequestWithRelations } from '@/models/request';
import { ClientArticlesList } from '@/routes/Brand/Requests/Request/components/shared/ClientArticlesList';
import ClientStepper from '@/routes/Brand/Requests/Request/components/shared/ClientStepper';
import useViewPort from '@/utils/useViewport';

export const Completed = ({ request }: { request: RequestWithRelations }) => {
  const hasFeedback = !!request.feedback && Object.keys(request.feedback).length > 0;

  return (
    <BrandRequestMainContent>
      <Stack gap="1.5rem">
        <ClientStepper request={request} />
        {!hasFeedback && <RequestCompletedMessage request={request} />}
        {hasFeedback && <FeedbackInfo request={request} />}
        <ClientArticlesList request={request} showPrice={!!request.client} showArticleComment />
      </Stack>
    </BrandRequestMainContent>
  );
};

const RequestCompletedMessage = ({ request }: { request: RequestWithRelations }) => {
  const { isMobile } = useViewPort();

  return (
    <Stack row gap="0.5rem">
      <IconShipmentDone className="color-primary-700" style={{ fontSize: '24px' }} />
      {request.client && (
        <Paragraph size={isMobile ? '100' : '50'}>
          <Trans id="store.request.completed.client-text">
            The <Plural value={request.articles.length} one="item has" other="items have" /> been
            successfully delivered back to the client.
          </Trans>
        </Paragraph>
      )}
    </Stack>
  );
};

const FeedbackInfo = ({ request }: { request: RequestWithRelations }) => {
  return (
    <Box gap="0.5rem" padding="16px">
      <Stack row gap="0.5rem">
        <InlineText size="50" color="secondary">
          <Trans id="store.request.completed.review.client">
            {request.client?.name} left a review
          </Trans>
        </InlineText>
        <Stack
          row
          style={{
            color: 'var(--color-warning-500)',
            height: '24px',
            display: 'inline-flex',
            lineHeight: '24px',
            fontSize: '20px',
          }}
          alignItems="center"
        >
          {([1, 2, 3, 4, 5] as const).map((rating) => (
            <IconStar key={rating} filled={rating <= (request.feedback?.global ?? 0)} />
          ))}
        </Stack>
      </Stack>
      <Stack row gap="0.25rem">
        {request.feedback?.quality != null && (
          <InlineText color="secondary">
            <Trans id="store.request.completed.review.quality">Quality:</Trans>{' '}
            {request.feedback?.quality}/5{' • '}
          </InlineText>
        )}
        {request.feedback?.speed != null && (
          <InlineText color="secondary">
            <Trans id="store.request.completed.review.speed">Speed:</Trans>{' '}
            {request.feedback?.speed}
            /5
            {' • '}
          </InlineText>
        )}
        {request.feedback?.communication != null && (
          <InlineText color="secondary">
            <Trans id="store.request.completed.review.communication">Communication:</Trans>{' '}
            {request.feedback?.communication}/5
          </InlineText>
        )}
      </Stack>
      {request.feedback?.comment && (
        <Paragraph
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          flex={1}
          style={{
            backgroundColor: 'var(--color-neutral-200)',
            borderRadius: '8px',
            padding: '8px 12px',
          }}
        >
          {request.feedback?.comment}
        </Paragraph>
      )}
    </Box>
  );
};
